import { useTranslation } from 'react-i18next';
import { ClipboardList } from 'lucide-react';
import { ScreenLink as Link } from '../permissions/ScreenLink';
import Badge from '../ui/Badge';
import { formatIls } from '../sales/shared';
import type { CharacterizationBanner as Banner } from '../../types';
import { characterizationHref, hoursMinutes, paidCount } from './characterizationModel';

/**
 * P-07 (D-74 §8) — inside the engagement opened from an "אפיון" card:
 *
 *   ┌ נפתח מאפיון "אתר חדש" · הושלם · 12:30 ש' (שורה נפרדת) · תשלומים 2/2 שולמו · [לכרטיס] ┐
 *
 * The card's hours are their own "אפיון" line — never in this engagement's
 * hours (D-74 §2). The price shows only to who may see the money.
 */
export default function CharacterizationBanner({ card }: { card: Banner }) {
  const { t } = useTranslation();
  const tone = card.status === 'completed' ? 'success' : card.status === 'cancelled' ? 'neutral' : 'info';
  return (
    <p className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-primary-100 bg-primary-50 px-4 py-2.5 text-sm text-surface-800" role="note">
      <ClipboardList size={16} className="text-primary-700 flex-shrink-0" aria-hidden="true" />
      <span className="font-medium">{t('characterization.banner', { title: card.title })}</span>
      <Badge tone={tone} dot>{t(`characterization.status.${card.status}`)}</Badge>
      <span className="tabular-nums text-surface-700">{t('characterization.bannerHours', { hours: hoursMinutes(card.minutes) })}</span>
      <span className="tabular-nums text-surface-700">{t('characterization.bannerPayments', { paid: paidCount(card.payments) })}</span>
      {card.price != null && (
        <span className="tabular-nums text-surface-700">{formatIls(card.price)} {t('characterization.beforeVat')}</span>
      )}
      <Link to={characterizationHref(card.organization_id, card.id)} className="font-medium text-primary-700 hover:underline underline-offset-2">
        {t('characterization.bannerOpen')}
      </Link>
    </p>
  );
}
